import { creatureIcon, creatureSprite } from "../art/creatures.ts";
import { enterAge, settleT } from "../art/craftFeel.ts";
import { TYPE_COL, UI, withA, type Col } from "../art/pal.ts";
import { moveById } from "../data/moves.ts";
import { SPECIES, type SpeciesId } from "../data/species.ts";
import { fitText, measureText, wrapPx } from "../engine/font.ts";
import { SCREEN_H, SCREEN_W, type Gfx } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import type { Scene, ScenePayload } from "../game/scenes.ts";
import { COSMETICS, ECON, sumCuring, type AuctionView, type CosmeticId, type CrownListingView, type CrownView, type EconView } from "../net/economy.ts";
import type { ClientMsg } from "../net/protocol.ts";
import { humanCheck } from "../net/captcha.ts";
import { backdrop, focus, glass, hint, titleBar, typeBadge } from "../ui/kit.ts";

const ROWS = 6;
export const HOUSE_TABS = ["AUCTIONS", "MARKET", "VAULT", "SHOP", "HALL"] as const;
type Tab = (typeof HOUSE_TABS)[number];

export const BRASS: Col = [226, 172, 86, 255];
const BRASS_DK: Col = [120, 74, 28, 255];
const BRASS_HI: Col = [255, 226, 160, 255];

/** "3d 4h", "2h 10m" or "4:05". */
export function span(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  if (m >= 1440) return `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

/** A small brass ring, the Brass mark. */
export function brassMark(g: Gfx, x: number, y: number): void {
  g.disc(x + 4.5, y + 4.5, 4.5, BRASS_DK);
  g.disc(x + 4.5, y + 4, 4, BRASS);
  g.disc(x + 3.5, y + 3, 1.5, BRASS_HI);
  g.disc(x + 4.5, y + 4, 1.6, BRASS_DK);
}

/** "#007 Neuralith" for rows; the full label lives on the card. */
function short(c: CrownView): string {
  return `#${String(c.serial).padStart(3, "0")} ${SPECIES[c.species as SpeciesId]?.name ?? c.species}`;
}

function cosmeticDesc(id: CosmeticId): string {
  return COSMETICS.find((c) => c.id === id)?.desc ?? "";
}

type Row =
  | { kind: "crown"; crown: CrownView; right?: string; rightCol?: Col; auction?: AuctionView; listing?: CrownListingView }
  | { kind: "register" }
  | { kind: "key" }
  | { kind: "cosmetic"; id: CosmeticId; name: string; price: number; owned: boolean };

type Modal =
  | { kind: "bid"; auction: AuctionView; amount: number }
  | { kind: "price"; crown: CrownView; price: number }
  | { kind: "actions"; crown: CrownView; i: number }
  | { kind: "gift"; crown: CrownView; i: number }
  | { kind: "confirm"; title: string; lines: string[]; yes: string; msg: ClientMsg }
  | { kind: "register"; step: 0 | 1; i: number; keyOnly?: boolean }
  | { kind: "restore"; keyOnly?: boolean };

/**
 * The Crown House (Neon Harbor). Crowns are numbered legends felled in Crown Raids: this is where
 * their 24-hour auctions run (the price goes to the raiders who felled them), where scouts resell
 * them to each other, keep them in a vault, show them off, or rest them in the Hall. Brass, the
 * house currency, is only ever earned from raid pots and never bought: every fee here burns.
 */
export function crownHouseScene(ctx: Ctx): Scene {
  let tab: Tab = "AUCTIONS";
  let cursor = 0;
  let scroll = 0;
  let age = 0;
  let poll = 0;
  let gotHouse = 0;
  let gotEcon = 0;
  let modal: Modal | null = null;
  let toast: { text: string; at: number } | null = null;
  let entry: HTMLInputElement | null = null;
  let unsub: () => void = () => {};

  const econ = (): EconView | null => ctx.net.econ;
  const live = (): boolean => ctx.net.live && econ()?.mode !== "off";
  const available = (): number => econ()?.wallet.available ?? 0;

  function say(text: string): void {
    toast = { text, at: ctx.now };
  }

  function rows(): Row[] {
    const e = econ();
    const h = ctx.net.house;
    if (!e) return [];
    if (tab === "AUCTIONS") {
      return (h?.auctions ?? []).map((a) => ({
        kind: "crown", crown: a.crown, auction: a,
        right: a.top ? `${a.top.amount}` : `${a.minNext}`, rightCol: a.top?.mine ? UI.green : a.top ? BRASS_HI : UI.textDim,
      }));
    }
    if (tab === "MARKET") {
      return (h?.listings ?? []).map((l) => ({ kind: "crown", crown: l.crown, listing: l, right: `${l.price}`, rightCol: l.mine ? UI.teal : BRASS_HI }));
    }
    if (tab === "VAULT") {
      const mine: Row[] = e.crowns.map((c) => ({ kind: "crown", crown: c, right: e.showcase.includes(c.oid) ? "SHOW" : undefined, rightCol: UI.teal }));
      const listed: Row[] = (h?.listings ?? []).filter((l) => l.mine).map((l) => ({ kind: "crown", crown: l.crown, listing: l, right: `${l.price}`, rightCol: UI.teal }));
      return e.wallet.registered ? [...mine, ...listed, { kind: "key" }] : [{ kind: "register" }];
    }
    if (tab === "SHOP") return COSMETICS.map((c) => ({ kind: "cosmetic", id: c.id, name: c.name, price: c.price, owned: e.cosmetics.includes(c.id) }));
    return (h?.hall ?? []).map((c) => ({ kind: "crown", crown: c, right: c.lastPrice !== null ? `${c.lastPrice}` : undefined, rightCol: UI.textDim }));
  }

  function clampCursor(): void {
    const n = rows().length;
    cursor = Math.max(0, Math.min(cursor, n - 1));
    if (cursor < scroll) scroll = cursor;
    if (cursor >= scroll + ROWS) scroll = cursor - ROWS + 1;
    scroll = Math.max(0, Math.min(scroll, Math.max(0, n - ROWS)));
  }

  function send(msg: ClientMsg): void {
    if (!ctx.net.send(msg)) say("Not connected. Try again in a moment.");
  }

  function closeEntry(): void {
    entry?.remove();
    entry = null;
  }

  /** A DOM field over the canvas for pasting a recovery key (same trick as the name screen). */
  function openEntry(): void {
    const host = typeof document !== "undefined" && typeof document.createElement === "function" ? document.getElementById("bezel") : null;
    if (!host) return;
    entry = document.createElement("input");
    entry.id = "key-entry";
    entry.placeholder = "MC1-...";
    entry.autocomplete = "off";
    entry.spellcheck = false;
    entry.addEventListener("keydown", (ev) => {
      ev.stopPropagation();
      if (ev.key === "Enter") {
        const ok = ctx.net.restore(entry!.value);
        ctx.audio[ok ? "confirm" : "fail"]();
        closeEntry();
        modal = null;
        say(ok ? "Switching to that account..." : "That is not a recovery key.");
      } else if (ev.key === "Escape") {
        ctx.audio.back();
        closeEntry();
        const keyOnly = modal?.kind === "restore" && modal.keyOnly;
        modal = { kind: "register", step: 1, i: keyOnly ? 1 : 2, keyOnly };
      }
    });
    host.appendChild(entry);
    setTimeout(() => entry?.focus(), 0);
  }

  function copyKey(): void {
    const key = ctx.net.recoveryKey();
    const clip = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
    if (!clip) return say("Copy is blocked here: write the key down by hand.");
    clip.writeText(key).then(() => say("Recovery key copied. Keep it somewhere safe."), () => say("Copy is blocked here: write the key down by hand."));
  }

  function vaultActions(c: CrownView): string[] {
    const shown = econ()?.showcase.includes(c.oid);
    return ["List for sale", shown ? "Take off showcase" : "Put on showcase", "Gift to a scout", `Enshrine (${ECON.enshrineFee} Brass)`];
  }

  function peers(): { id: string; name: string }[] {
    // Names are not unique: each scout shows the tag the server gave them (ask your friend for theirs,
    // it is on their LIVE WORLD panel). Nearest scouts come first.
    const d = (p: { x: number; y: number }) => Math.max(Math.abs(p.x - ctx.state.x), Math.abs(p.y - ctx.state.y));
    return [...ctx.net.peers.values()].sort((a, b) => d(a) - d(b) || a.name.localeCompare(b.name))
      .map((p) => ({ id: p.id, name: p.tag ? `${p.name} #${p.tag}` : p.name }));
  }

  function activate(row: Row): void {
    const e = econ()!;
    if (row.kind === "register") {
      ctx.audio.confirm();
      modal = { kind: "register", step: e.wallet.seal.adult ? 1 : 0, i: 0 };
      return;
    }
    if (row.kind === "key") {
      ctx.audio.confirm();
      modal = { kind: "register", step: 1, i: 0, keyOnly: true };
      return;
    }
    if (row.kind === "cosmetic") {
      if (row.owned) return say("Already yours.");
      if (!e.wallet.registered) return say("Register in the VAULT tab first.");
      const total = available() + sumCuring(e.wallet);
      if (total < row.price) return say(`${row.name} costs ${row.price} Brass.`);
      ctx.audio.confirm();
      modal = { kind: "confirm", title: `Buy ${row.name}?`, lines: [cosmeticDesc(row.id), `${row.price} Brass, burned. Curing Brass can pay.`], yes: "buy", msg: { t: "cosmeticBuy", sku: row.id } };
      return;
    }
    const c = row.crown;
    if (row.auction) {
      const a = row.auction;
      if (a.opensIn > 0) return say(`Bidding opens in ${span(a.opensIn - (ctx.now - gotHouse))}.`);
      const own = a.top?.mine ? a.top.amount : 0;
      if (available() + own < a.minNext) return say(`You need ${a.minNext} spendable Brass to bid.`);
      ctx.audio.confirm();
      modal = { kind: "bid", auction: a, amount: a.minNext };
      return;
    }
    if (row.listing) {
      const l = row.listing;
      ctx.audio.confirm();
      if (l.mine) {
        const fee = Math.max(ECON.cancelMin, Math.ceil((l.price * ECON.cancelPct) / 100));
        modal = { kind: "confirm", title: `Take back ${short(c)}?`, lines: [`Unlisting costs ${fee} Brass (burned).`], yes: "take back", msg: { t: "crownUnlist", id: l.id } };
      } else {
        const fee = Math.ceil((l.price * ECON.feePct) / 100);
        modal = { kind: "confirm", title: `Buy ${short(c)}?`, lines: [`${l.price} Brass to ${l.sellerName}.`, `${fee} of it burns as the house fee.`], yes: "buy", msg: { t: "crownBuy", id: l.id, expect: l.price } };
      }
      return;
    }
    if (c.where === "mine") {
      ctx.audio.confirm();
      modal = { kind: "actions", crown: c, i: 0 };
      return;
    }
    ctx.audio.cursor();
  }

  function updateModal(m: Modal): void {
    const d = ctx.input.menuDir(ctx.now);
    const back = ctx.input.consume("Escape");
    const ok = ctx.input.consume("Enter");
    if (m.kind === "restore") {
      // The DOM field handles its own keys; without one (no page to host it), B still gets out.
      if (!entry && back) {
        ctx.audio.back();
        modal = { kind: "register", step: 1, i: m.keyOnly ? 1 : 2, keyOnly: m.keyOnly };
      }
      return;
    }
    if (m.kind === "bid" || m.kind === "price") {
      const big = ctx.input.down.has("Shift") ? 10 : 1;
      const step = d?.y ? -d.y * 10 * big : d?.x ? d.x * 100 * big : 0;
      if (m.kind === "bid") {
        const own = m.auction.top?.mine ? m.auction.top.amount : 0;
        const max = Math.max(m.auction.minNext, available() + own);
        if (step) { m.amount = Math.max(m.auction.minNext, Math.min(max, m.amount + step)); ctx.audio.cursor(); }
        if (back) { ctx.audio.back(); modal = null; return; }
        if (ok) {
          ctx.audio.confirm();
          send({ t: "crownBid", id: m.auction.id, amount: m.amount, expect: m.auction.top?.amount ?? 0 });
          modal = null;
        }
      } else {
        if (step) { m.price = Math.max(ECON.listMin, Math.min(ECON.listMax, m.price + step)); ctx.audio.cursor(); }
        if (back) { ctx.audio.back(); modal = { kind: "actions", crown: m.crown, i: 0 }; return; }
        if (ok) {
          ctx.audio.confirm();
          send({ t: "crownList", oid: m.crown.oid, price: m.price });
          modal = null;
        }
      }
      return;
    }
    if (m.kind === "actions" || m.kind === "gift" || m.kind === "register") {
      const n = m.kind === "actions" ? 4 : m.kind === "gift" ? peers().length : m.step === 0 ? 2 : m.keyOnly ? 3 : 4;
      if (d?.y && n) { m.i = (m.i + d.y + n) % n; ctx.audio.cursor(); }
      if (back) {
        ctx.audio.back();
        modal = m.kind === "gift" ? { kind: "actions", crown: m.crown, i: 2 } : null;
        return;
      }
      if (!ok) return;
      if (m.kind === "actions") {
        const c = m.crown;
        if (m.i === 0) { ctx.audio.confirm(); modal = { kind: "price", crown: c, price: Math.max(ECON.listMin, c.lastPrice ?? 300) }; }
        else if (m.i === 1) {
          ctx.audio.confirm();
          const cur = econ()?.showcase ?? [];
          const next = cur.includes(c.oid) ? cur.filter((o) => o !== c.oid) : [c.oid, ...cur].slice(0, ECON.showcaseSlots);
          send({ t: "showcase", oids: next });
          modal = null;
        } else if (m.i === 2) {
          if (!peers().length) { ctx.audio.fail(); say("No other scouts online to gift to."); return; }
          ctx.audio.confirm();
          modal = { kind: "gift", crown: c, i: 0 };
        } else {
          ctx.audio.confirm();
          modal = { kind: "confirm", title: `Enshrine ${short(c)}?`, lines: ["It rests in the Hall of Crowns for good,", `with your name. ${ECON.enshrineFee} Brass, burned.`], yes: "enshrine", msg: { t: "crownEnshrine", oid: c.oid } };
        }
        return;
      }
      if (m.kind === "gift") {
        const to = peers()[m.i];
        if (!to) return;
        ctx.audio.confirm();
        modal = { kind: "confirm", title: `Gift ${short(m.crown)} to ${to.name}?`, lines: [`The notary charges ${ECON.notaryFee} Brass (burned).`, "They need a Scout Seal to receive it."], yes: "gift", msg: { t: "crownGift", oid: m.crown.oid, to: to.id } };
        return;
      }
      // Registration.
      if (m.step === 0) {
        if (m.i === 0) { ctx.audio.confirm(); modal = { kind: "register", step: 1, i: 0 }; }
        else { ctx.audio.back(); modal = null; }
        return;
      }
      const pick = m.keyOnly ? m.i + 1 : m.i;
      if (pick === 0) {
        ctx.audio.confirm();
        modal = null;
        const key = ctx.net.captchaKey;
        if (!key) send({ t: "econRegister", adult: true, recovery: true });
        else {
          say("One quick human check...");
          humanCheck(key).then((captcha) => send({ t: "econRegister", adult: true, recovery: true, captcha }), (e: Error) => say(e.message));
        }
      } else if (pick === 1) {
        copyKey();
      } else if (pick === 2) {
        ctx.audio.confirm();
        modal = { kind: "restore", keyOnly: m.keyOnly };
        openEntry();
      } else {
        ctx.audio.back();
        modal = null;
      }
      return;
    }
    // Generic confirm.
    if (back) { ctx.audio.back(); modal = null; return; }
    if (ok) {
      ctx.audio.confirm();
      send(m.msg);
      modal = null;
    }
  }

  function drawCrownCard(g: Gfx, rx: number, row: Extract<Row, { kind: "crown" }>): void {
    const c = row.crown;
    const id = c.species as SpeciesId;
    const sp = SPECIES[id];
    if (!sp) return;
    const glow = ctx.reduced ? 0.5 : 0.5 + 0.5 * Math.sin(ctx.now / 400);
    g.disc(rx + 38, 82, 30 + glow * 2, withA(TYPE_COL[sp.types[0]!]!.fill, 60));
    g.disc(rx + 38, 82, 24, withA(BRASS, 40 + Math.round(glow * 30)));
    g.blit(creatureSprite(id, "front", 2, c.shiny), rx + 6, 50);
    g.textShadow(rx + 76, 52, fitText(`${sp.name}${c.shiny ? " ✦" : ""}`, 96), c.shiny ? UI.goldHi : UI.text, UI.ink);
    g.text(rx + 76, 63, `S${c.season} #${String(c.serial).padStart(3, "0")}  Lv${c.level}`, BRASS_HI);
    g.text(rx + 164, 63, c.verified ? "✓" : "×", c.verified ? UI.green : UI.red);
    let bx = rx + 76;
    for (const type of sp.types) bx += typeBadge(g, bx, 74, type, true) + 2;
    g.text(rx + 76, 89, `DRIP ${c.drip.hp + c.drip.atk + c.drip.def + c.drip.spd}/60`, UI.textDim);
    g.text(rx + 76, 100, fitText(`by ${c.felledBy} +${Math.max(0, c.raiders - 1)}`, 96), UI.textDim);
    c.moves.slice(0, 4).forEach((mid, k) => {
      const mv = moveById(mid);
      g.rrect(rx + 8 + (k % 2) * 84, 114 + Math.floor(k / 2) * 14, 80, 12, 4, withA(TYPE_COL[mv.type]!.fill, 90));
      g.text(rx + 12 + (k % 2) * 84, 117 + Math.floor(k / 2) * 14, fitText(mv.name, 72), UI.text);
    });
    brassMark(g, rx + 9, 146);
    const since = ctx.now - gotHouse;
    if (row.auction) {
      const a = row.auction;
      const line = a.top ? `${a.top.amount} by ${a.top.mine ? "you" : a.top.name}` : `opens at ${a.minNext}`;
      g.textShadow(rx + 22, 147, fitText(line, 96), a.top?.mine ? UI.green : BRASS_HI, UI.ink);
      g.textR(rx + 170, 147, a.opensIn > since ? `in ${span(a.opensIn - since)}` : span(a.endsIn - since), UI.textDim);
      if (a.myShareBps > 0) {
        // What the server would actually pay: the share of 95%, capped at 35%, nothing if unregistered.
        const price = a.top?.amount ?? a.minNext;
        g.text(rx + 10, 158, fitText(econ()?.wallet.registered ? `Your cut at ${price}: ${a.myCut} Brass` : "You raided it: register to be paid", 160), UI.teal);
      }
      hint(g, rx + 10, 168, "A", a.top?.mine ? "raise your bid" : `bid ${a.minNext}+`, "a");
    } else if (row.listing) {
      const l = row.listing;
      g.textShadow(rx + 22, 147, `${l.price} Brass`, BRASS_HI, UI.ink);
      g.textR(rx + 170, 147, fitText(l.mine ? "your listing" : `by ${l.sellerName}`, 70), UI.textDim);
      hint(g, rx + 10, 164, "A", l.mine ? "take back" : "buy", "a");
    } else {
      const sold = c.lastPrice !== null ? `last sold ${c.lastPrice}` : "never sold";
      g.text(rx + 22, 147, `${sold} · ${c.owners} owner${c.owners === 1 ? "" : "s"}`, UI.textDim);
      if (c.where === "mine") hint(g, rx + 10, 164, "A", "list, show, gift, enshrine", "a");
      else g.text(rx + 10, 166, fitText(c.where !== "hall" ? "" : c.enshrinedBy ? `Enshrined by ${c.enshrinedBy}.` : "Unsold: it rests in the Hall.", 160), UI.textMute);
    }
  }

  function drawSeal(g: Gfx, rx: number, e: EconView): void {
    const w = e.wallet;
    const s = w.seal;
    g.textShadow(rx + 10, 50, w.registered ? "SCOUT SEAL" : "REGISTER TO EARN", UI.text, UI.ink);
    const items: [string, boolean, string][] = [
      ["18 or older", s.adult, s.adult ? "attested" : "A to attest"],
      ["Recovery key saved", s.recovery, s.recovery ? "saved" : "A to view"],
      ["Account age", s.ageDays >= s.needAgeDays, `${Math.min(s.ageDays, s.needAgeDays)}/${s.needAgeDays} days`],
      ["Active days", s.days >= s.needDays, `${Math.min(s.days, s.needDays)}/${s.needDays}`],
      ["Raids with a real share", s.raids >= s.needRaids, `${Math.min(s.raids, s.needRaids)}/${s.needRaids}`],
    ];
    items.forEach(([label, done, note], k) => {
      const y = 64 + k * 14;
      g.text(rx + 10, y, done ? "✓" : "•", done ? UI.green : UI.textMute);
      g.text(rx + 20, y, label, done ? UI.text : UI.textDim);
      g.textR(rx + 170, y, note, done ? UI.green : UI.textMute);
    });
    let y = 138;
    const lines = w.registered
      ? w.sealed ? ["Sealed: you can bid, buy, list and gift."] : ["Registered: raids pay you Brass.", "The Seal unlocks bidding and trading."]
      : ["Raids pay Brass only to registered", "scouts. It stays on the server."];
    for (const line of lines) { g.text(rx + 10, y, line, UI.textDim); y += 11; }
    if (!w.registered) hint(g, rx + 10, 164, "A", "register", "a");
  }

  return {
    id: "crownHouse",
    native: true,
    enter(payload?: ScenePayload) {
      age = enterAge(ctx.shot);
      const want = payload?.tab as Tab | undefined;
      tab = want && HOUSE_TABS.includes(want) ? want : "AUCTIONS";
      cursor = scroll = 0;
      poll = 0;
      modal = null;
      toast = null;
      gotHouse = gotEcon = ctx.now;
      unsub = ctx.net.on((m) => {
        if (m.t === "crownHouse") { gotHouse = ctx.now; clampCursor(); }
        if (m.t === "econ") { gotEcon = ctx.now; clampCursor(); }
        if (m.t === "notice") {
          say(m.text);
          ctx.net.notices = ctx.net.notices.filter((n) => n !== m.text);
        }
      });
      if (ctx.net.live) {
        ctx.net.send({ t: "econ" });
        ctx.net.send({ t: "crownHouse" });
      }
    },
    exit() {
      closeEntry();
      unsub();
    },
    update() {
      age += ctx.dt;
      poll += ctx.dt;
      if (ctx.net.live && poll > 10_000) {
        poll = 0;
        ctx.net.send({ t: "crownHouse" });
        ctx.net.send({ t: "econ" });
      }
      if (entry && typeof document !== "undefined" && document.activeElement !== entry) entry.focus();
      if (modal) { updateModal(modal); return; }
      if (!live() || !econ()) {
        if (ctx.input.consumeAny("Enter", "Escape")) { ctx.audio.back(); ctx.pop(); }
        return;
      }
      const d = ctx.input.menuDir(ctx.now);
      if (d?.x) {
        tab = HOUSE_TABS[(HOUSE_TABS.indexOf(tab) + d.x + HOUSE_TABS.length) % HOUSE_TABS.length]!;
        cursor = scroll = 0;
        ctx.audio.cursor();
        return;
      }
      const n = rows().length;
      if (d?.y && n) {
        cursor = (cursor + d.y + n) % n;
        clampCursor();
        ctx.audio.cursor();
      }
      if (ctx.input.consume("Escape")) { ctx.audio.back(); ctx.pop(); return; }
      if (!ctx.input.consume("Enter")) return;
      const row = rows()[cursor];
      if (!row) { ctx.audio.fail(); return; }
      activate(row);
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const t = settleT(age, reduced, 120);
      backdrop(g, ctx.now, reduced, [76, 52, 20, 255], [14, 10, 20, 255]);
      const e = econ();
      const curing = e ? sumCuring(e.wallet) : 0;
      titleBar(g, "CROWN HOUSE", e && e.mode !== "off" ? `${available()} Brass${curing ? ` +${curing} curing` : ""}` : undefined, BRASS);
      if (!ctx.net.live || !e || e.mode === "off") {
        glass(g, 56, 70, 272, 80, { r: 10, border: BRASS_DK });
        const head = !ctx.net.live ? "The Crown House is closed offline." : !e ? "Opening the ledger..." : "The Crown House is closed.";
        const body = !ctx.net.live
          ? "Crowns and Brass live on the world server. Connect to bid on Crowns felled in raids."
          : !e ? "One moment." : "This server runs without Crowns & Brass.";
        g.textShadow(70, 82, head, UI.text, UI.ink);
        let y = 98;
        for (const row of wrapPx(body, 244)) { g.text(70, y, row, UI.textDim); y += 11; }
        hint(g, 70, 134, "A", "back", "a");
        return;
      }
      // Tabs.
      const h = ctx.net.house;
      let tx = 8;
      for (const name of HOUSE_TABS) {
        const on = name === tab;
        const count = name === "AUCTIONS" ? h?.auctions.length ?? 0 : name === "MARKET" ? h?.listings.length ?? 0
          : name === "VAULT" ? e.crowns.length : name === "SHOP" ? COSMETICS.length : h?.hallTotal ?? h?.hall.length ?? 0;
        const label = `${name} ${count}`;
        const w = measureText(label) + 12;
        g.rrect(tx, 26, w, 14, 5, on ? BRASS : withA(UI.bg2, 220));
        g.text(tx + 6, 29, label, on ? UI.paperInk : UI.textDim);
        tx += w + 3;
      }
      if (e.season) g.textR(376, 29, `S${e.season.n} ${e.season.eve ? "TIDE EVE" : `· ${span(e.season.endsIn - (ctx.now - gotEcon))}`}`, e.season.eve ? UI.pink : UI.textMute);

      // Left: rows.
      const list = rows();
      const lx = 8 - Math.round((1 - t) * 30);
      glass(g, lx, 44, 184, 136, { r: 8 });
      if (!list.some((r) => r.kind !== "key")) {
        const empty = tab === "AUCTIONS" ? "No Crown on the block. Crown Raids at the Raid Gate put one up for 24 hours."
          : tab === "MARKET" ? "No Crowns for sale. Owners list theirs from the VAULT."
            : tab === "VAULT" ? "No Crowns yet. Win an auction, or raid: every sale pays the raiders."
              : "The Hall is empty. Unsold and enshrined Crowns rest here.";
        let y = list.length ? 76 : 58;
        for (const row of wrapPx(empty, 164)) { g.text(lx + 10, y, row, UI.textMute); y += 11; }
      }
      for (let r = 0; r < ROWS; r++) {
        const i = scroll + r;
        const row = list[i];
        if (!row) break;
        const y = 50 + r * 21;
        const on = cursor === i;
        g.rrect(lx + 6, y, 172, 19, 5, on ? withA(UI.bg3, 220) : withA(UI.bg1, 160));
        if (row.kind === "crown") {
          g.blit(creatureIcon(row.crown.species as SpeciesId, row.crown.shiny), lx + 9, y + 1);
          g.text(lx + 30, y + 6, fitText(`${on ? ">" : ""}${short(row.crown)}${row.crown.shiny ? " ✦" : ""}`, row.right ? 100 : 140), on ? UI.goldHi : UI.text);
          if (row.right) g.textR(lx + 172, y + 6, row.right, row.rightCol ?? BRASS_HI);
        } else if (row.kind === "register" || row.kind === "key") {
          brassMark(g, lx + 12, y + 5);
          g.text(lx + 28, y + 6, `${on ? ">" : ""}${row.kind === "key" ? "Recovery key" : "Register to earn Brass"}`, on ? UI.goldHi : UI.text);
        } else {
          g.text(lx + 12, y + 6, fitText(`${on ? ">" : ""}${row.name}`, 110), on ? UI.goldHi : row.owned ? UI.textDim : UI.text);
          g.textR(lx + 172, y + 6, row.owned ? "OWNED" : `${row.price}`, row.owned ? UI.green : BRASS_HI);
        }
        if (on) focus(g, lx + 5, y - 1, 174, 21, ctx.now, reduced, 6);
      }
      if (scroll > 0) g.text(lx + 88, 45, "▲", UI.textDim);
      if (scroll + ROWS < list.length) g.text(lx + 88, 174, "▼", UI.textDim);

      // Right: detail card.
      const rx = 198 + Math.round((1 - t) * 30);
      glass(g, rx, 44, 178, 136, { r: 8, border: withA(BRASS_DK, 255) });
      const row = list[cursor];
      if (row?.kind === "crown") drawCrownCard(g, rx, row);
      else if (row?.kind === "register" || row?.kind === "key") drawSeal(g, rx, e);
      else if (row?.kind === "cosmetic") {
        g.textShadow(rx + 10, 52, row.name, UI.text, UI.ink);
        let y = 68;
        for (const line of wrapPx(cosmeticDesc(row.id), 156)) { g.text(rx + 10, y, line, UI.textDim); y += 11; }
        g.text(rx + 10, y + 6, "Priced in Brass and fully burned.", UI.textMute);
        g.text(rx + 10, y + 17, "Curing Brass can pay for it.", UI.textMute);
        brassMark(g, rx + 9, 146);
        g.textShadow(rx + 22, 147, row.owned ? "Yours for good." : `${row.price} Brass`, row.owned ? UI.green : BRASS_HI, UI.ink);
        if (!row.owned) hint(g, rx + 10, 164, "A", "buy", "a");
      } else if (tab === "VAULT" && e.wallet.registered) {
        drawSeal(g, rx, e);
      } else {
        g.text(rx + 12, 60, "Nothing selected.", UI.textMute);
      }

      // Footer: controls on the left, the wallet story on the right.
      hint(g, 8, SCREEN_H - 30, "◀▶", "tabs", "a");
      hint(g, 8, SCREEN_H - 16, "B", "leave", "b");
      const lot = e.wallet.curing[0];
      const status = e.mode === "shadow"
        ? `Shadow season: nothing is minted yet. You would have ${e.wallet.shadow} Brass.`
        : e.season?.eve ? `Tide Eve: buys pause. At season end the Tide burns half of Brass above ${ECON.tideFree}.`
          : e.season && e.season.endsIn < 7 * 86_400_000 ? `The Tide in ${span(e.season.endsIn - (ctx.now - gotEcon))}: half of Brass above ${ECON.tideFree} burns.`
          : e.wallet.pendingPoints ? `${e.wallet.pendingPoints} raid points pay out at the top of the hour.`
            : lot ? `${curing} Brass curing: first lot frees in ${span(lot.until - Date.now())}.`
              : !e.wallet.registered ? "Raids pay registered scouts. Every fee burns."
                : !e.wallet.sealed ? "Earn your Scout Seal to bid, buy, list and gift."
                  : "Fees here all burn. Raiders share every auction.";
      let sy = SCREEN_H - 30;
      for (const line of wrapPx(status, 250).slice(0, 2)) { g.textR(376, sy, line, e.mode === "shadow" ? UI.pink : UI.textDim); sy += 11; }

      if (modal) drawModal(g, modal);
      if (toast && ctx.now - toast.at < 3500) {
        const w = Math.min(360, measureText(toast.text) + 20);
        glass(g, (SCREEN_W - w) / 2, 186, w, 16, { r: 6, alpha: 235 });
        g.text((SCREEN_W - w) / 2 + 10, 190, fitText(toast.text, w - 20), UI.text);
      }
    },
  };

  function menu(g: Gfx, x: number, y: number, opts: string[], i: number, w = 200): void {
    opts.forEach((o, k) => {
      const on = k === i;
      if (on) g.rrect(x - 4, y + k * 13 - 2, w, 12, 4, withA(UI.bg3, 220));
      g.text(x, y + k * 13, `${on ? "▶ " : "  "}${o}`, on ? UI.goldHi : UI.text);
    });
  }

  function drawModal(g: Gfx, m: Modal): void {
    g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, 150]);
    if (m.kind === "bid" || m.kind === "price") {
      glass(g, 70, 52, 244, 112, { r: 10, border: BRASS });
      const value = m.kind === "bid" ? m.amount : m.price;
      g.textShadow(84, 62, m.kind === "bid" ? `Bid on ${short(m.auction.crown)}` : `List ${short(m.crown)} for`, UI.text, UI.ink);
      brassMark(g, 84, 84);
      g.textBig(98, 80, `${value}`, BRASS_HI, 2);
      g.text(98 + measureText(`${value}`, 2) + 6, 88, "Brass", UI.textDim);
      if (m.kind === "bid") {
        const own = m.auction.top?.mine ? m.auction.top.amount : 0;
        g.text(84, 104, `Held until you are outbid, then refunded.`, UI.textDim);
        g.text(84, 115, `Spendable ${available() + own}. Next minimum ${m.auction.minNext}.`, UI.textMute);
      } else {
        const fee = Math.ceil((m.price * ECON.feePct) / 100);
        g.text(84, 104, `You get ${m.price - fee}, cured 24h. ${fee} burns.`, UI.textDim);
        g.text(84, 115, "Taking it back later costs 1%.", UI.textMute);
      }
      g.text(84, 128, "▲▼ 10   ◀▶ 100   Shift ×10", UI.textMute);
      hint(g, 84, 144, "A", m.kind === "bid" ? "place bid" : "list it", "a");
      hint(g, 170, 144, "B", "cancel", "b");
      return;
    }
    if (m.kind === "actions") {
      glass(g, 92, 56, 200, 104, { r: 10, border: BRASS });
      g.textShadow(104, 64, fitText(m.crown.label, 176), UI.text, UI.ink);
      menu(g, 108, 82, vaultActions(m.crown), m.i, 176);
      hint(g, 104, 142, "B", "back", "b");
      return;
    }
    if (m.kind === "gift") {
      const ps = peers();
      const shown = ps.slice(Math.max(0, m.i - 5), Math.max(0, m.i - 5) + 6);
      glass(g, 92, 44, 200, 124, { r: 10, border: BRASS });
      g.textShadow(104, 52, `Gift to which scout?`, UI.text, UI.ink);
      menu(g, 108, 70, shown.map((p) => p.name), m.i - Math.max(0, m.i - 5), 176);
      hint(g, 104, 154, "B", "back", "b");
      return;
    }
    if (m.kind === "confirm") {
      glass(g, 64, 62, 256, 92, { r: 10, border: BRASS });
      g.textShadow(78, 72, fitText(m.title, 228), UI.text, UI.ink);
      m.lines.forEach((line, k) => g.text(78, 90 + k * 11, fitText(line, 228), UI.textDim));
      hint(g, 78, 134, "A", m.yes, "a");
      hint(g, 78 + measureText(m.yes) + 40, 134, "B", "no", "b");
      return;
    }
    if (m.kind === "restore") {
      glass(g, 20, 58, 344, 100, { r: 10, border: BRASS });
      g.textShadow(34, 66, "Restore an account from its recovery key", UI.text, UI.ink);
      g.text(34, 80, "Paste the key and press Enter. This device switches to", UI.textDim);
      g.text(34, 91, "that account (write this one's key down first).", UI.textDim);
      g.text(34, 142, "Enter: restore   Esc: back", UI.textMute);
      return;
    }
    // Registration.
    glass(g, 28, 34, 328, 142, { r: 10, border: BRASS });
    if (m.step === 0) {
      g.textShadow(42, 44, "Crowns & Brass: before you earn", UI.text, UI.ink);
      let y = 60;
      for (const line of wrapPx(`Brass is a game currency: it cannot be bought or cashed out. Crowns are traded for Brass between scouts. Each season ends with the Tide, which burns half of any balance above ${ECON.tideFree}. Earning and trading are for players 18 or older.`, 300)) {
        g.text(42, y, line, UI.textDim);
        y += 11;
      }
      menu(g, 46, 118, ["I am 18 or older", "Not now"], m.i, 290);
      hint(g, 42, 160, "A", "choose", "a");
      return;
    }
    g.textShadow(42, 44, "Your recovery key", UI.text, UI.ink);
    const key = ctx.net.recoveryKey();
    g.rrect(40, 56, 304, 26, 5, withA(UI.ink, 220));
    g.text(46, 60, key.slice(0, 44), BRASS_HI);
    g.text(46, 71, key.slice(44), BRASS_HI);
    let y = 88;
    for (const line of wrapPx("Your Brass and Crowns live on the server under this key. Write it down: a lost device without it loses them. Anyone who has it owns them.", 300)) {
      g.text(42, y, line, UI.textDim);
      y += 11;
    }
    menu(g, 46, 124, m.keyOnly ? ["Copy the key", "Restore another key", "Close"] : ["I saved it: register", "Copy the key", "Restore another key", "Not now"], m.i, 150);
  }
}
